/**
 * Document seed data generator
 */

import { faker } from '@faker-js/faker'
import { Document, Project, FileType } from '@/types'

const documentTypes = [
  FileType.PDF,
  FileType.Document,
  FileType.Spreadsheet,
  FileType.Presentation,
  FileType.Image,
  FileType.File,
]

const documentNames = [
  'Project Brief',
  'Design Mockups',
  'Technical Specification',
  'Meeting Notes',
  'Budget Spreadsheet',
  'Client Contract',
  'Brand Guidelines',
  'User Research Report',
  'Wireframes',
  'Style Guide',
  'Project Timeline',
  'Deliverables Checklist',
]

/**
 * Generate a single document
 */
export function generateDocument(project: Project): Document {
  const creator = faker.helpers.arrayElement(project.users)
  const type = faker.helpers.arrayElement(documentTypes)

  // Select 1-3 users to share with
  const sharedUsers = faker.helpers.arrayElements(
    project.users,
    faker.number.int({ min: 1, max: Math.min(3, project.users.length) })
  )

  // Ensure creator is included
  if (!sharedUsers.find((u) => u.id === creator.id)) {
    sharedUsers.push(creator)
  }

  const extension = getFileExtension(type)
  const filename = `${faker.helpers.arrayElement(documentNames).toLowerCase().replace(/\s/g, '-')}-${faker.number.int({ min: 1, max: 999 })}${extension}`

  return {
    id: faker.string.uuid(),
    createdAt: faker.date.past({ years: 0.5 }),
    updatedAt: faker.date.recent({ days: 14 }),
    creator,
    name: faker.helpers.arrayElement(documentNames),
    description: faker.lorem.sentence(),
    url: `/documents/${filename}`,
    type,
    workspace: project.workspace,
    users: sharedUsers,
    tasks: [],
    projects: [project],
    messages: [],
  }
}

/**
 * Generate multiple documents for a project
 */
export function generateDocuments(project: Project, count: number = 8): Document[] {
  const documents: Document[] = []

  for (let i = 0; i < count; i++) {
    documents.push(generateDocument(project))
  }

  return documents
}

/**
 * Get file extension based on document type
 */
function getFileExtension(type: FileType): string {
  switch (type) {
    case FileType.PDF:
      return '.pdf'
    case FileType.Document:
      return '.docx'
    case FileType.Spreadsheet:
      return '.xlsx'
    case FileType.Presentation:
      return '.pptx'
    case FileType.Image:
      return '.jpg'
    case FileType.File:
      return '.txt'
    default:
      return '.file'
  }
}
